(() => {
  if (window.ZBFunctionalFixesReady) return;
  window.ZBFunctionalFixesReady = true;

  const rootPath = window.Shopify?.routes?.root || '/';
  const endpoint = (path) => `${rootPath}${path}`.replace(/\/{2,}/g, '/');

  const requestJSON = async (url, options = {}) => {
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json', ...(options.body ? { 'Content-Type': 'application/json' } : {}) },
      ...options
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || data.message || 'Unable to update the cart.');
    return data;
  };

  const normalizeProperties = (properties = {}) => Object.keys(properties || {})
    .filter((key) => properties[key] !== null && properties[key] !== '')
    .sort()
    .reduce((result, key) => { result[key] = String(properties[key]); return result; }, {});
  const propertiesMatch = (left, right) => JSON.stringify(normalizeProperties(left)) === JSON.stringify(normalizeProperties(right));
  const sellingPlanId = (item) => item?.selling_plan_allocation?.selling_plan?.id ?? item?.selling_plan_allocation?.selling_plan_id ?? null;
  const findMatchingLine = (cart, variantId, lineData) => cart.items?.find((item) => {
    const sameVariant = String(item.variant_id) === String(variantId);
    const sameProperties = propertiesMatch(item.properties, lineData.properties || {});
    const expectedPlan = lineData.sellingPlan ? String(lineData.sellingPlan) : null;
    const actualPlan = sellingPlanId(item) ? String(sellingPlanId(item)) : null;
    return sameVariant && sameProperties && expectedPlan === actualPlan;
  });

  const readPickerData = (picker) => {
    const variants = JSON.parse(picker.querySelector('[data-zb-variants]')?.textContent || '[]');
    const lineData = JSON.parse(picker.querySelector('[data-zb-line-data]')?.textContent || '{}');
    const selectedOptions = Array.from(picker.querySelectorAll('[data-zb-option]')).map((select) => select.value);
    const variant = variants.find((candidate) => Array.isArray(candidate.options) && candidate.options.length === selectedOptions.length && candidate.options.every((value, index) => String(value) === String(selectedOptions[index])));
    return { variant, lineData };
  };

  const refreshPickerState = (picker) => {
    const button = picker.querySelector('[data-zb-variant-update]');
    const status = picker.querySelector('[data-zb-variant-status]');
    if (!button || picker.dataset.zbFixBusy === 'true') return;
    try {
      const { variant } = readPickerData(picker);
      const unchanged = variant && String(variant.id) === String(picker.dataset.currentVariantId);
      button.disabled = !variant || !variant.available || unchanged;
      if (!variant) { if (status) status.textContent = 'This size and design combination is unavailable.'; }
      else if (!variant.available) { if (status) status.textContent = 'This size and design combination is sold out.'; }
      else if (unchanged) { if (status) status.textContent = ''; }
      else if (status) status.textContent = 'Selection ready to update.';
    } catch {
      button.disabled = true;
      if (status) status.textContent = 'Unable to read this selection. Please refresh and try again.';
    }
  };

  const setPickerBusy = (picker, busy) => {
    picker.dataset.zbFixBusy = busy ? 'true' : 'false';
    picker.querySelectorAll('select, button').forEach((control) => {
      if (busy) { control.dataset.zbWasDisabled = control.disabled ? 'true' : 'false'; control.disabled = true; }
      else { control.disabled = control.dataset.zbWasDisabled === 'true'; delete control.dataset.zbWasDisabled; }
    });
  };

  const updateCartVariant = async (button) => {
    const picker = button.closest('[data-zb-variant-picker]');
    if (!picker || picker.dataset.zbFixBusy === 'true') return;
    const status = picker.querySelector('[data-zb-variant-status]');
    let variant; let lineData;
    try { ({ variant, lineData } = readPickerData(picker)); }
    catch { if (status) status.textContent = 'Unable to read this selection. Please refresh and try again.'; return; }
    if (!variant || !variant.available) { if (status) status.textContent = 'This size and design combination is unavailable.'; return; }
    if (String(variant.id) === String(picker.dataset.currentVariantId)) { if (status) status.textContent = 'This selection is already in your bag.'; return; }

    const oldKey = picker.dataset.lineKey;
    const quantity = Math.max(1, Number(picker.dataset.quantity || 1));
    let addedKey = null; let previousQuantity = 0; let oldRemoved = false;
    setPickerBusy(picker, true);
    if (status) status.textContent = 'Updating selection…';

    try {
      const beforeCart = await requestJSON(endpoint('cart.js'));
      const existingTarget = findMatchingLine(beforeCart, variant.id, lineData);
      previousQuantity = existingTarget?.quantity || 0;
      const payload = { id: variant.id, quantity, properties: lineData.properties || {} };
      if (lineData.sellingPlan) payload.selling_plan = lineData.sellingPlan;
      const addedState = await requestJSON(endpoint('cart/add.js'), { method: 'POST', body: JSON.stringify({ items: [payload] }) });
      const addedItem = addedState.items?.find((item) => String(item.variant_id ?? item.id) === String(variant.id)) || addedState.items?.[0] || addedState;
      addedKey = addedItem?.key || existingTarget?.key || null;
      await requestJSON(endpoint('cart/change.js'), { method: 'POST', body: JSON.stringify({ id: oldKey, quantity: 0 }) });
      oldRemoved = true;
      const afterCart = await requestJSON(endpoint('cart.js'));
      const targetLine = findMatchingLine(afterCart, variant.id, lineData);
      const oldLineStillExists = afterCart.items?.some((item) => item.key === oldKey);
      if (!targetLine || oldLineStillExists) throw new Error('The selected option could not be confirmed.');
      sessionStorage.setItem('zbCartSelectionUpdated', 'true');
      window.location.reload();
    } catch (error) {
      if (oldRemoved) { window.location.reload(); return; }
      if (addedKey) {
        try { await requestJSON(endpoint('cart/change.js'), { method: 'POST', body: JSON.stringify({ id: addedKey, quantity: previousQuantity }) }); }
        catch { /* A refresh will reconcile the cart if rollback is unavailable. */ }
      }
      if (status) status.textContent = error.message || 'Unable to update this selection. Please try again.';
      setPickerBusy(picker, false);
      refreshPickerState(picker);
    }
  };

  const applyPrepublishContentCorrections = () => {
    const hero = document.querySelector('[data-zb-hero-slider]');
    if (hero) {
      hero.querySelectorAll('.zb-hero-explainer__card, .zb-hero-mobile-explainer__definition').forEach((card) => {
        const title = card.querySelector('h3')?.textContent?.trim().toUpperCase();
        const fact = card.querySelector('.zb-hero-explainer__fact');
        const text = card.querySelector('.zb-hero-explainer__text');
        if (title === 'BORN PREMATURELY') {
          if (fact) fact.innerHTML = '<span aria-hidden="true"></span>Before 37 weeks';
          if (text) text.textContent = 'A preterm (preemie) baby is born before 37 completed weeks of pregnancy.';
        }
        if (title === 'LOW BIRTH WEIGHT') {
          if (fact) fact.innerHTML = '<span aria-hidden="true"></span>Below 2.5 kg';
          if (text) text.textContent = 'Low birth weight means a birth weight below 2.5 kg, regardless of gestational age.';
        }
        if (title === 'NEWBORN') {
          if (fact) fact.innerHTML = '<span aria-hidden="true"></span>First 28 days';
          if (text) text.textContent = 'A newborn (neonate) is a baby during the first 28 days after birth, whether born preterm or full term.';
        }
      });
      hero.querySelectorAll('.zb-hero-slider__trust li').forEach((item) => {
        const strong = item.querySelector('strong');
        const small = item.querySelector('small');
        const title = strong?.textContent?.trim();
        if (title === 'Doctor & Parent') { strong.textContent = 'Thoughtful Design'; if (small) small.textContent = 'For Easier Dressing'; }
        if (title === 'Easy 7-Day') { strong.textContent = 'Clear Return'; if (small) small.textContent = 'Support'; }
      });
    }

    const sizeRows = {
      'Micro Preemie': ['0.5–1.1','1.10–2.43','23–30'],
      'Preemie': ['1.1–1.8','2.43–3.97','30–38'],
      'Prem 1': ['1.8–2.3','3.97–5.07','38–44'],
      'Prem 2': ['2.3–2.7','5.07–5.95','44–50'],
      'Early Baby': ['2.7–3.2','5.95–7.05','50–53'],
      'Newborn': ['3.2–4.0','7.05–8.82','53–56'],
      '0–3 Months': ['4.0–5.7','8.82–12.57','56–62'],
      '3–6 Months': ['5.7–7.6','12.57–16.76','62–68'],
      '6–9 Months': ['7.6–9.2','16.76–20.28','68–74'],
      '9–12 Months': ['9.2–10.5','20.28–23.15','74–80']
    };
    document.querySelectorAll('.zb-size-chart__table tbody tr').forEach((row) => {
      const cells = row.querySelectorAll('th, td');
      const values = sizeRows[cells[0]?.textContent?.trim()];
      if (values && cells.length >= 4) { cells[1].textContent = values[0]; cells[2].textContent = values[1]; cells[3].textContent = values[2]; }
    });

  };

  document.addEventListener('change', (event) => {
    const sortSelect = event.target.closest('[data-zb-sort]');
    if (sortSelect) {
      event.preventDefault(); event.stopPropagation();
      const url = new URL(window.location.href);
      if (sortSelect.value) url.searchParams.set('sort_by', sortSelect.value); else url.searchParams.delete('sort_by');
      url.searchParams.delete('page'); window.location.assign(url.toString()); return;
    }
    const filterSelect = event.target.closest('[data-zb-filter-select]');
    if (filterSelect?.value) { event.preventDefault(); event.stopPropagation(); window.location.assign(filterSelect.value); return; }
    const picker = event.target.closest('[data-zb-variant-picker]');
    if (picker && event.target.matches('[data-zb-option]')) refreshPickerState(picker);
  }, true);

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-zb-variant-update]');
    if (!button || button.disabled) return;
    event.preventDefault(); event.stopPropagation(); event.stopImmediatePropagation(); updateCartVariant(button);
  }, true);

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-zb-variant-picker]').forEach(refreshPickerState);
    applyPrepublishContentCorrections();
    if (sessionStorage.getItem('zbCartSelectionUpdated') === 'true') {
      sessionStorage.removeItem('zbCartSelectionUpdated');
      const status = document.querySelector('[data-zb-cart-status]');
      if (status) status.textContent = 'Selection updated.';
    }
  }, { once: true });
})();

